<script lang="ts">
  import {
    createPlayer,
    type FoundationDestroy,
    PlayerEvent,
    type PlayerInterface,
    type SessionStartEvent,
    type FoundationError,
  } from '@foundation-player/loader';
  import { afterUpdate, getContext, onDestroy, onMount, setContext } from 'svelte';
  import { writable } from 'svelte/store';
  import Loading from '../../shared/assets/icons/Loading.svelte';
  import { FocusManager } from '../../shared/focus-manager/FocusManager';
  import { PlayerStore } from '../../shared/player-store/PlayerStore';
  import type { PlayerLoaderConfig } from '../../shared/types/player-loader-config';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import { generateUniqueId } from '../../shared/utils/generate-unique-id';
  import { getCodeFromEvent } from '../../shared/utils/keyboard-event';
  import type { MediaModeOptions } from '../types/internal/media-mode';
  import { type UIStateContext } from '../types/internal/ui-state';
  import type { Theme, UIConfig } from '../types/ui-config';
  import AdvertisementLayer from './AdvertisementLayer.svelte';
  import ContentLayer from './ContentLayer.svelte';
  import ErrorLayer from '../../shared/components/ErrorLayer.svelte';
  import TextTrackLayer from '../../shared/components/TextTrackLayer.svelte';
  import NextMediumPreviewLayer from './NextMediumPreviewLayer.svelte';
  import { mediaModeOptionsAudio, mediaModeOptionsVideo } from '../media-mode-options';
  import type { MediaMode } from '../../shared/types/media-mode';

  interface HTMLElementWithConfigInfo extends HTMLElement {
    getUIConfig: () => UIConfig | undefined;
  }

  export let loaderConfig: PlayerLoaderConfig;
  export let uiConfig: UIConfig;

  let resolvePlayerPromise: (player: PlayerInterface) => void;
  let rejectPlayerPromise: (error: unknown) => void;
  const playerPromise = new Promise<PlayerInterface>((resolve, reject) => {
    resolvePlayerPromise = resolve;
    rejectPlayerPromise = reject;
  });

  /**
   * Returns a promise that resolves to the created instance of the Foundation
   * Player.
   */
  export function getPlayer(): Promise<PlayerInterface> {
    return playerPromise;
  }

  const playerStore = new PlayerStore(uiConfig);
  const {
    commercialPlaying,
    interactiveAdPlaying,
    linearAdPlaying,
    poster,
    headline,
    sessionState,
    sponsoredAdPlaying,
    stalling,
  } = playerStore;

  const focusManager = new FocusManager();
  setContext<FocusManager>('focusManager', focusManager);

  $: loading = $sessionState === 'loading';
  $: waitingForPlayback =
    $sessionState === 'not-yet-created' || $sessionState === 'loading' || $sessionState === 'ready';
  $: shouldShowAdvertisementLayer = ($commercialPlaying || $sponsoredAdPlaying) && !$interactiveAdPlaying;

  setContext<PlayerStateContext>('playerState', playerStore);
  setContext<UIConfig>('uiConfig', uiConfig);

  /**
   * Whether any kind of interaction happened, either by the user or when the
   * player started by any other means (autoplay or by script)
   */
  const waitingForFirstInteraction = writable<boolean>(true);

  const nextMediumPreviewCurrentlyShown = writable<boolean>(false);

  const fullscreenEventsOnlyInFullscreen = writable<boolean>(false);

  setContext<UIStateContext>('uiState', {
    waitingForFirstInteraction,
    nextMediumPreviewCurrentlyShown,
    fullscreenEventsOnlyInFullscreen,
  });

  let player: PlayerInterface | undefined;
  let playerElement: HTMLElement;
  let fullscreenElement: HTMLElement;
  let nonLinearElement: HTMLElement;
  let videoElement: HTMLVideoElement;

  let theme: Theme = uiConfig.theme || 'dark';
  let mode: MediaMode = uiConfig.mode || 'video';
  let aspectRatio = (uiConfig.aspectRatio || '16:9').replace(':', '/');

  /* Prevent duplicate ids as it would cause errors with multiple instances on one page */
  const idSuffix = generateUniqueId();
  const loadingIndicatorId = `loading-spinner-${idSuffix}`;
  const stallingIndicatorId = `stalling-spinner-${idSuffix}`;
  const createPlayerOnDemand = uiConfig.createPlayerOnDemand ?? false;

  let playerId: string;
  let mediaSessionId: string | undefined;
  let errorCode: number | undefined;
  let errorOccurred = false;
  let playerIsDestroyed = false;

  setContext<MediaModeOptions>('mediaModeOptions', mode === 'audio' ? mediaModeOptionsAudio : mediaModeOptionsVideo);
  setContext<MediaMode>('mediaMode', mode);

  const { hasPreviewNextMedium } = getContext<MediaModeOptions>('mediaModeOptions');

  const showErrorLayer = (error: FoundationError) => {
    errorOccurred = true;
    errorCode = error.code || undefined;
  };

  const togglePlay = () => {
    if (!player) {
      return;
    }

    if (player.isPlaying()) {
      player.pause();
    } else {
      player.play();
    }
  };

  const onSessionStart = (event: SessionStartEvent) => {
    mediaSessionId = event.id;
  };

  const updateFocus = () => {
    // We need setTimeout here because otherwise the fullscreen button is not yet available when
    // being called in 'afterUpdate'.
    // This seems to be the case because the fullscreen component checks if fullscreenSupported
    // before adding the <IconButton> component.
    setTimeout(() => {
      focusManager.updateFocusIfNeeded();
    }, 0);
  };

  // During advertisements we show a button on top of the video when it is being paused. When this
  // button gets activated the UI changes but the state does not. Therefore the 'afterUpdate' does
  // not get triggered and we have to manually trigger the updating.
  // TODO: Refactor that the focus gets updated automatically when it changes (by the
  // focus-manager), rather than manually updating the focus. Probably makes most sense after
  // having switched to Svelte 5 because lifecycles and related functionality changed.
  const setFocusWhenPlaying = () => {
    if ($linearAdPlaying) {
      updateFocus();
    }
  };

  const createFoundationPlayer = async () => {
    try {
      loading = true;
      player = await createPlayer({
        ...loaderConfig,
        playerElementOrId: playerElement,
        videoElementOrId: videoElement,
      });
      player.getAdvertisingAdapter()?.setNonLinearElement(nonLinearElement);
      player.addEventListener(PlayerEvent.Playing, setFocusWhenPlaying);
      player.addEventListener(PlayerEvent.SessionStart, onSessionStart);
      player.addEventListener(PlayerEvent.Destroy, onPlayerDestroy);

      playerId = player.getId();

      playerStore.initializeStateFromPlayer(player);

      resolvePlayerPromise(player);
    } catch (error) {
      rejectPlayerPromise(error);
      loading = false;

      errorOccurred = true;

      if (error instanceof Error && 'code' in error && typeof error.code === 'number') {
        errorCode = error.code;
      } else {
        // 50000: Internal player error
        errorCode = 50000;
      }
    }
  };

  onMount(async () => {
    if (!createPlayerOnDemand) {
      await createFoundationPlayer();
    }

    (fullscreenElement as HTMLElementWithConfigInfo).getUIConfig = () => uiConfig;
  });

  const onPlayerDestroy = (event: FoundationDestroy) => {
    playerIsDestroyed = true;

    if (event.error) {
      showErrorLayer(event.error);
    }
  };

  afterUpdate(() => {
    updateFocus();
  });

  onDestroy(() => {
    if (player) {
      player.removeEventListener(PlayerEvent.Playing, setFocusWhenPlaying);
      player.removeEventListener(PlayerEvent.SessionStart, onSessionStart);
      player.removeEventListener(PlayerEvent.Destroy, onPlayerDestroy);
    }
  });

  const togglePlayerOnEnterOrSpace = (event: KeyboardEvent) => {
    const code = getCodeFromEvent(event);

    if (code === 'Enter' || code === 'Space') {
      event.preventDefault();

      if (player) {
        togglePlay();
      } else if (uiConfig.createPlayerOnDemand) {
        createFoundationPlayer();
      }
    }
  };
</script>

<!--
 We set attributes for mode and theme, so we can use attribute selectors in
 child components (we cannot use classes because they get modified on a
 component basis).

 For color changes depending on mode and theme, we set CSS variables here which
 we then use in the components.

 For example
  :global([data-mode="audio"]) .my-class {}
  :global([data-theme="light"]) .my-class {}
  :global([data-mode="audio"][data-theme="light"]) .my-class {}
-->

<!--
 We do some very specialized things here to improve keyboard support. Svelte
 does not know that we provide alternative support for mouse/touch users, so we
 have to disable some accessibility checks.
-->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-no-noninteractive-tabindex -->
<div
  class="fullscreen-element"
  data-mode={mode}
  data-theme={theme}
  bind:this={fullscreenElement}
  tabindex="0"
  lang="de"
  role="region"
  aria-label={$headline ? `Medien-Player ${$headline}` : 'Medien-Player'}
  aria-describedby={loading ? loadingIndicatorId : $stalling ? stallingIndicatorId : undefined}
  aria-busy={loading || $stalling}
  on:keydown|self={togglePlayerOnEnterOrSpace}
>
  <div
    class="non-linear-element"
    bind:this={nonLinearElement}
    style={mode === 'video' ? `aspect-ratio: ${aspectRatio}` : null}
  >
    <div class="player-wrapper">
      <div class="player-element" bind:this={playerElement}>
        <!-- svelte-ignore a11y-media-has-caption -->
        <video aria-label={$headline} bind:this={videoElement} disablePictureInPicture></video>
        <!-- Refering to the "interactiveAdPlaying": Before and during VPAID ads Player-Events
          are not emitted (except for a few Ad-Events). That means our "sessionState" does not
          get updated, because there is no "MediaStarted"-Event if the VPAID-ad is the first
          one in an adSlot. Because of this we have to handle this case seperately to prevent
          our poster from overlaying the player. -->
        {#if mode === 'video' && $poster && waitingForPlayback && !$interactiveAdPlaying}
          <div class="poster" style:background-image="url({$poster})" />
        {/if}
      </div>

      {#if player}
        <div class="text-track-layer">
          <TextTrackLayer {player} />
        </div>
      {/if}

      {#if !$nextMediumPreviewCurrentlyShown}
        <div
          class="content-layer"
          class:has-error={errorOccurred}
          class:linear-ad-playing={$linearAdPlaying}
          class:player-is-destroyed={playerIsDestroyed}
          data-testid="player-level-content-layer"
        >
          <ContentLayer {player} {fullscreenElement} onStart={!player ? createFoundationPlayer : undefined} />
        </div>
      {/if}

      {#if shouldShowAdvertisementLayer && player}
        <div class="advertisement-layer">
          <AdvertisementLayer {player} {fullscreenElement} />
        </div>
      {/if}

      {#if hasPreviewNextMedium && player}
        <NextMediumPreviewLayer {player} />
      {/if}

      {#if loading}
        <div class="loading-overlay">
          <Loading id={loadingIndicatorId} label="Player wird geladen" />
        </div>
      {/if}

      {#if $stalling}
        <div class="stalling-overlay">
          <Loading id={stallingIndicatorId} label="Medium wird gepuffert" />
        </div>
      {/if}

      {#if errorOccurred}
        <ErrorLayer {playerId} {mediaSessionId} {errorCode} />
      {/if}
    </div>
  </div>
</div>

<style module>
  .fullscreen-element {
    /*
     * Define css variables that can be used here and in all child components.
     * They are not meant to be set by the integrator. Maybe later...
     *
     * Older browsers, for example on set-top boxes, may not support css
     * variables, so we have to provide fallbacks.
     * We could just use the fallbacks only, however by also using CSS variables
     * we allow customisation in modern browsers.
     */
    --primary-color: #f0f0f0;
    --accent-color: #ffffff;
    --disabled-color: #424447;
    --advertisement-color: #f1bf42;
    --focus-background: #000000;
    --focus-color: #ffffff;

    --controls-contrast-background: rgba(24, 24, 24, 0.75);
    --controls-no-transparency-background: rgb(24, 24, 24);

    --slider-bar-color: rgba(255, 255, 255, 0.5);
    --slider-track-color: var(--primary-color);
    --slider-knob-color: var(--primary-color);
    --slider-hover-bar-color: var(--slider-bar-color);
    --slider-hover-track-color: var(--accent-color);
    --slider-hover-knob-color: var(--accent-color);

    --icon-color: var(--primary-color);
    --icon-background-color: transparent;
    --icon-focus-color: var(--focus-color);
    --icon-focus-background-color: var(--focus-background);
    --icon-hover-color: var(--focus-color);
    --icon-hover-background-color: var(--focus-background);
    --icon-disabled-color: var(--disabled-color);
    --icon-disabled-background-color: var(--icon-background-color);
    --icon-contrast-outline: var(--outline);

    --button-color: var(--primary-color);
    --button-background-color: var(--disabled-color);
    --button-hover-color: var(--focus-background);
    --button-hover-background-color: var(--focus-color);
    --button-contrast-outline: var(--outline);

    --menu-background-color: #000000;
    --menu-border-color: #5a5a5a;
    --menu-item-hover-background-color: rgba(255, 255, 255, 0.2);

    --error-layer-background-color: #000000;
    --error-layer-color: #ffffff;

    --tooltip-background: #000000;
    --tooltip-color: #ffffff;

    --next-medium-preview-background-color: #000000;

    --outline: 2px solid var(--accent-color);

    color-scheme: dark light;
  }

  .fullscreen-element[data-theme='light'] {
    --primary-color: #000000;
    --accent-color: #141414;
    --focus-color: var(--primary-color);
    --focus-background: #ffffff;
    --controls-contrast-background: rgba(224, 224, 224, 0.75);
    --controls-no-transparency-background: rgb(224, 224, 224);
    --slider-bar-color: #e6e6e6;
    --menu-background-color: #ffffff;
    --menu-border-color: #cdcdcd;
    --menu-item-hover-background-color: rgba(0, 0, 0, 0.1);
    --error-layer-background-color: #ffffff;
    --error-layer-color: #000000;
    --tooltip-color: var(--primary-color);
    --tooltip-background: #ffffff;
  }

  /* In audio mode, invert the focus and hover styles plus restrict loading-spinner */
  .fullscreen-element[data-mode='audio'] {
    --focus-color: #000000;
    --focus-background: #ffffff;
    --icon-contrast-outline: none;
    --tooltip-color: #000000;
    --tooltip-background: #ffffff;
    --loading-spinner-max-size: 88px;
  }

  /* In audio mode with, invert the focus and hover styles */
  .fullscreen-element[data-mode='audio'][data-theme='light'] {
    --focus-color: #ffffff;
    --focus-background: #000000;
    --tooltip-color: #ffffff;
    --tooltip-background: #000000;
  }

  /* For users which prefer a higher contrast or reduced transparency, we set
   * non-transparent colors (here and in other components).
   * Normally, we would prefer to set non-transparent colors as default and only
   * switch to transparency when the user sets no-preference to both, but then
   * we would have to use an AND combination, which would break on browsers that
   * don't yet support prefers-reduced-transparency.
   */
  @media (prefers-contrast: more), (prefers-reduced-transparency: reduce) {
    .fullscreen-element {
      --menu-item-hover-background-color: #333;
      --slider-bar-color: #808080;
    }

    .fullscreen-element[data-theme='light'] {
      --menu-item-hover-background-color: #ccc;
      --slider-bar-color: #808080;
    }
  }

  .fullscreen-element {
    max-width: 100vw;
    max-height: 100vh;
    position: relative;
    /* The black background helps to see the player if it has not yet been loaded or started. */
    background-color: black;

    color: #f5f5f5; /* Fallback for older browsers */
    color: var(--primary-color, #f5f5f5);

    font-family: Roboto, sans-serif;

    /* If the integrator set a lower font size on the root element, we don't
     * want that to affect the ui. So we use at least 16px */
    font-size: 16px;
    font-size: max(16px, 1rem);
  }

  .fullscreen-element:focus {
    /* we set an inset border instead to keep the focus marker within our element */
    outline: none;
  }

  /* Our focus marker, a pseudo element with a border. This way, the outline is
   * kept within the area or our element. */
  .fullscreen-element:focus::after {
    content: '';
    position: absolute;
    inset: 2px;
    border: 2px solid #ffffff;
    border-color: var(--accent-color);
    /* very important, otherwise this pseudo element would catch all clicks etc. */
    pointer-events: none;
  }

  @supports selector(:focus-visible) {
    /* Don't show the pseudo element on focus... */
    .fullscreen-element:focus::after {
      content: none;
    }

    /* ...but show it on focus-visible */
    .fullscreen-element:focus-visible::after {
      content: '';
    }
  }

  .fullscreen-element :global(*) {
    box-sizing: border-box;
  }

  .fullscreen-element[data-theme='light'] {
    background-color: white;
  }

  /*
    The nonLinearContainer is the one where the non-linear ads are placed. In case
    of an L-shaped ad, it is required that this element has an aspect ratio of
    16/9, otherwise the ad would be stretched.
   */
  [data-mode='video'] > .non-linear-element {
    /* keep this as a fallback in case the passed in aspectRatio is invalid */
    aspect-ratio: 16/9;
    margin: auto;
    max-height: 100vh;
    position: relative;
    overflow: hidden;
    display: flex;
  }

  /*
    For browsers that do not support the aspect-ratio property (Chrome < 88,
    Firefox < 89, Safari < 15), we use the padding-bottom hack to set the height
    depending on the width.
    Unfortunately, this hack only works when the width is the limiting factor.
    For an aspect ratio larger than 16/9 (e.g. 2.35:1, cinema format), the height
    would be too large. The height still gets limited to 100vh, but then the
    aspect ration is no longer 16/9.
    We hope that in reality this is not relevant: We only have to support older
    browsers on TVs and set-top boxes, which likely run on devices with an aspect
    ratio of 16/9.
   */
  [data-mode='video'] > .non-linear-element::before {
    content: '';
    padding-bottom: 56.25%;
    float: left;
  }

  /*
    When in fullscreen and the aspect ratio of the viewport is smaller than 16:9
    (for example 16:10), vertically center the non-linear-element.
   */
  .fullscreen-element:fullscreen > .non-linear-element {
    transform: translateY(calc(50vh - 50%));
  }

  .player-wrapper {
    max-width: 100vw;
    max-height: 100vh;
    position: relative;
    flex: 1;
    container-type: inline-size;
    container-name: player-wrapper;
  }

  [data-mode='audio'] .player-wrapper {
    display: flex;
    flex-direction: column;
  }

  .text-track-layer {
    position: absolute;
    left: 0;
    bottom: 7%;
    width: 100%;

    display: flex;
    box-sizing: border-box;
  }

  /* We don't know how many lines are needed for the text track layer. To avoid
   * jumping of the controls, we show the text tracks below the controls.
   */
  [data-mode='audio'] .text-track-layer {
    order: 1;
  }

  .player-element {
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    isolation: isolate;

    /* Bitmovin sets a min-width of 260px, which conflicts with 9:16 videos on
       screens with a small height < 462px (the right part of the video would be
       cropped). To keep the video size within the viewport size, we remove the
       min-width. Thereby, the video neither gets cropped nor does it grow
       larger than the viewport, which both is important especially for ads.
       See https://rtltech.atlassian.net/browse/PLAYWEBUI-221?focusedCommentId=1441765
       for more information.
     */
    min-width: initial !important;

    /* Force the background color to black, needed if the video is not 9/16 and
     * a player instance was destroyed before.
     *
     * The foundation player sets a transparent background, bitmovin sets it to
     * black afterwards. If the player gets destroyed, the foundation player
     * styles get removed. If another instance gets created, the foundation
     * player styles are set again, this time overwriting the bitmovin styles.
     * To keep a consistent background color, we set it ourselves.
     *
     * See https://rtltech.atlassian.net/browse/PLAYHTML5-823
     */
    background-color: black !important;
  }

  [data-mode='audio'] .player-element {
    /*
      Bitmovin sets the min-dimensions to 260x150, an aspect ratio of 16/9 and
      width and height to 100%.
      We don't want to let the element grow, so we force it to take no space.
     */
    min-width: initial !important;
    min-height: initial !important;
    width: 1px !important;
    height: 1px !important;
    /* Hide the video element to prevent the picture-in-picture button from showing */
    visibility: hidden;
  }

  .player-element video {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .poster {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
  }

  [data-mode='video'] .content-layer {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .advertisement-layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .loading-overlay,
  .stalling-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .stalling-overlay {
    /* we want to let all events through so the user can still
    e.g. show and hide the content-layer on interaction */
    pointer-events: none;
    color: #f0f0f0;
    color: var(--primary-color);
  }

  /* TODO: still needs to be implemented for audio because design is unclear */
  [data-mode='audio'] .stalling-overlay {
    display: none;
  }

  .content-layer.linear-ad-playing,
  .content-layer.player-is-destroyed,
  .content-layer.has-error {
    visibility: hidden;
  }
</style>
